<script lang="ts" setup>
import { GpAlert, GpProgress, GpTooltip } from '@gportal/uikit';
import { type UserNodeLimit } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import Size from 'gpcore-uikit/interfaces/Size';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    nodeLimit: UserNodeLimit;
    size?: Size;
  }>(),
  {
    size: Size.Medium,
  },
);

const progressType = computed<'success' | 'danger' | 'warning' | 'primary'>(() => {
  if (props.nodeLimit.current < props.nodeLimit.limit) {
    return 'success';
  }

  if (props.nodeLimit.limit === props.nodeLimit.current) {
    return 'danger';
  }

  if (props.nodeLimit.limit - props.nodeLimit.current <= 3) {
    return 'warning';
  }

  return 'primary';
});

const progressSize = computed(() => {
  switch (props.size) {
    case Size.Tiny:
      return 'sm';
    case Size.Medium:
      return 'md';
    case Size.Large:
      return 'lg';
    default:
      return 'sm';
  }
});
</script>

<template>
  <GpAlert v-if="nodeLimit.limit === 0" alert-type="info" class="tw-mb-4">
    Please contact our <ExternalLink :url="$config.support.url">sales team</ExternalLink> to onboard
    your account. We are looking forward to get you started.
  </GpAlert>
  <p class="tw-mb-4" v-else>
    See your personal node limit, which affects all your projects. If you like to increase your
    limit, please contact our support.
  </p>

  <GpTooltip
    :content="`You have ${nodeLimit.limit - nodeLimit.current} nodes of ${
      nodeLimit.limit
    } available.`"
    placement="top"
    trigger-class="tw-w-full"
    class="tw-mt-4 tw-w-full"
  >
    <GpProgress
      class="tw-w-full"
      :size="progressSize"
      :progress-type="progressType"
      :value="nodeLimit.current"
      :value-max="nodeLimit.limit"
    />
  </GpTooltip>
</template>
